import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdvancedFilters from '../../components/admin/AdvancedFilters';
import api, { getAll } from '../../services/api';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { notify } from '../../utils/notifications';
import {
  Plus, Search, Eye, Edit, Trash2, Phone, Mail, MapPin, X, Send, Copy, CheckCircle
} from 'lucide-react';

// Valore speciale del menu Scuola: non può coincidere con il nome di un
// istituto vero, quindi non rischia di sovrapporsi ai dati reali.
const SENZA_ISTITUTO = '__SENZA_ISTITUTO__';

const Clienti = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const navigate = useNavigate();
  const [clienti, setClienti] = useState([]);
  const [filteredClienti, setFilteredClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    codice_fiscale: '',
    email: '',
    telefono: '',
    telefono_secondario: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    note: ''
  });

  useEffect(() => {
    caricaClienti();
  }, []);

  const caricaClienti = async () => {
    try {
      setLoading(true);
      const clientiData = await getAll('/anagrafiche/titolari/');
      setClienti(clientiData);
      setFilteredClienti(clientiData); // Inizializza filteredClienti
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
      notify.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      navigate(`/admin/clienti/${client.id}`);
    } else {
      setEditingClient(null);
      setFormData({
        nome: '',
        cognome: '',
        codice_fiscale: '',
        email: '',
        telefono: '',
        telefono_secondario: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        note: ''
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      nome: '',
      cognome: '',
      codice_fiscale: '',
      email: '',
      telefono: '',
      telefono_secondario: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      note: ''
    });
  };

  const normalizzaTelefono = (telefono) => {
    // Rimuovi spazi, trattini e il + iniziale se presente
    let tel = telefono.replace(/[\s-+]/g, '');
    
    // Se inizia con 0039, rimuovilo (verrà aggiunto +39)
    if (tel.startsWith('0039')) {
      tel = tel.substring(4);
    }
    // Se inizia con 39, rimuovilo (verrà aggiunto +39)
    else if (tel.startsWith('39') && tel.length > 10) {
      tel = tel.substring(2);
    }
    
    // Aggiungi sempre +39
    return '+39' + tel;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Il telefono è facoltativo: si normalizza solo se valorizzato
    const telefonoNormalizzato = formData.telefono ? normalizzaTelefono(formData.telefono) : null;
    const email = formData.email.trim().toLowerCase();

    // Prepara i dati senza creare ancora il cliente
    const datiCliente = {
      ...formData,
      email,
      telefono: telefonoNormalizzato
    };

    // Simula la creazione per generare password temporanea e messaggio WhatsApp
    // Ma non salva ancora nel database
    const passwordTemp = 'Casella' + Math.floor(1000 + Math.random() * 9000);

    // Messaggio WhatsApp - solo testo formattato (le emoji causano problemi di encoding)
    const messaggioWhatsApp = `*Benvenuto in MyCasella*

Ciao *${datiCliente.nome} ${datiCliente.cognome}*!

Le tue credenziali di accesso:
• *Email*: ${email}
• *Password*: ${passwordTemp}

Al primo accesso ti verrà chiesto di creare una nuova password.

*Accedi su:*
https://my.casellanoleggiobus.it

_Casella Noleggio Bus - Il tuo viaggio inizia qui_`;

    // Senza telefono non c'è WhatsApp: le credenziali si consegnano a mano
    const whatsappUrl = telefonoNormalizzato
      ? `https://wa.me/${telefonoNormalizzato.replace(/\+/g, '')}?text=${encodeURIComponent(messaggioWhatsApp)}`
      : null;

    // Prepara oggetto cliente simulato per il modal WhatsApp
    setNuovoCliente({
      ...datiCliente,
      password_temporanea: passwordTemp,
      messaggio_whatsapp: {
        messaggio: messaggioWhatsApp,
        whatsapp_url: whatsappUrl,
        telefono: telefonoNormalizzato,
        email,
        password: passwordTemp
      }
    });
    
    // Chiudi form e mostra modal WhatsApp
    setShowModal(false);
    setShowWhatsAppModal(true);
  };

  const handleConfirmCliente = async () => {
    // Ora effettivamente crea il cliente nel database
    const toastId = notify.loading('Creazione cliente...');
    
    try {
      await api.post('/anagrafiche/titolari/', nuovoCliente);
      notify.update(toastId, { type: 'success', render: 'Cliente creato con successo!' });
      
      // Ricarica lista clienti e chiudi modal
      await caricaClienti();
      setShowWhatsAppModal(false);
      setNuovoCliente(null);
      setFormData({
        nome: '',
        cognome: '',
        codice_fiscale: '',
        email: '',
        telefono: '',
        telefono_secondario: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        note: ''
      });
    } catch (error) {
      console.error('Errore salvataggio cliente:', error);
      const errorMsg = error.response?.data?.email?.[0] || error.response?.data?.telefono?.[0] || error.response?.data?.detail || error.message || 'Errore nella creazione del cliente';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleCopyCredenziali = () => {
    if (nuovoCliente) {
      const testo = `Email: ${nuovoCliente.email}\nPassword: ${nuovoCliente.password_temporanea}`;
      navigator.clipboard.writeText(testo);
      notify.success('Credenziali copiate!');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/anagrafiche/titolari/${id}/`);
      await caricaClienti();
      notify.update(toastId, { type: 'success', render: 'Cliente eliminato!' });
    } catch (error) {
      console.error('Errore eliminazione cliente:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione' });
    }
  };

  const handleApplyFilters = (filterValues) => {
    let filtered = [...clienti];

    // Filtro per testo di ricerca
    if (filterValues.search) {
      const searchLower = filterValues.search.toLowerCase();
      filtered = filtered.filter(client =>
        `${client.nome} ${client.cognome}`.toLowerCase().includes(searchLower) ||
        client.codice_fiscale?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.telefono?.includes(filterValues.search) ||
        client.scuola?.toLowerCase().includes(searchLower) ||
        (client.gruppi || []).some(g =>
          (g.nome || '').toLowerCase().includes(searchLower) ||
          (g.codice || '').toLowerCase().includes(searchLower)
        )
      );
    }

    // Stato dell'account: chi ha già scelto la propria password
    if (filterValues.stato_account) {
      const attivato = filterValues.stato_account === 'attivato';
      filtered = filtered.filter(client => !!client.primo_accesso_completato === attivato);
    }

    // Gruppo di linea: il cliente vi appartiene tramite i suoi abbonamenti
    if (filterValues.gruppo?.length > 0) {
      filtered = filtered.filter(client =>
        (client.gruppi || []).some(g => filterValues.gruppo.includes(g.codice))
      );
    }

    // Scuola: si confronta l'etichetta del gruppo, non il testo grezzo
    if (filterValues.scuola?.length > 0) {
      const vuoleSenza = filterValues.scuola.includes(SENZA_ISTITUTO);
      filtered = filtered.filter((client) => {
        const grezzo = (client.scuola || '').trim();
        if (!grezzo) return vuoleSenza;
        return filterValues.scuola.includes(scuole.perValore.get(grezzo));
      });
    }

    // Presenza di un abbonamento
    if (filterValues.abbonamento) {
      const conAbbonamento = filterValues.abbonamento === 'con';
      filtered = filtered.filter(client =>
        ((client.numero_abbonamenti || 0) > 0) === conAbbonamento
      );
    }

    // Città e provincia: selezione multipla, confronto senza distinzione
    // di maiuscole perché i dati inseriti non sono uniformi
    if (filterValues.citta?.length > 0) {
      filtered = filtered.filter(client =>
        filterValues.citta.includes((client.citta || '').trim().toUpperCase())
      );
    }
    if (filterValues.provincia?.length > 0) {
      filtered = filtered.filter(client =>
        filterValues.provincia.includes((client.provincia || '').trim().toUpperCase())
      );
    }

    // Mensilità non saldate il cui periodo è già passato
    if (filterValues.arretrati) {
      filtered = filtered.filter(client => client.ha_arretrati);
    }

    if (filterValues.senza_telefono) {
      filtered = filtered.filter(client => !client.telefono);
    }

    if (filterValues.con_foto) {
      filtered = filtered.filter(client => client.foto);
    }

    // Anagrafica incompleta: manca almeno un dato utile a contattare
    // o identificare il cliente
    if (filterValues.anagrafica_incompleta) {
      filtered = filtered.filter(client =>
        !client.codice_fiscale || !client.indirizzo || !client.citta || !client.provincia
      );
    }

    setFilteredClienti(filtered);
  };

  // Città e province si ricavano dai clienti presenti: erano campi a testo
  // libero, ma i valori reali sono pochi e con maiuscole incoerenti
  const valoriDistinti = (campo) => {
    const mappa = new Map();
    clienti.forEach((c) => {
      const grezzo = (c[campo] || '').trim();
      if (!grezzo) return;
      const chiave = grezzo.toUpperCase();
      if (!mappa.has(chiave)) mappa.set(chiave, { value: chiave, label: grezzo.toUpperCase(), n: 0 });
      mappa.get(chiave).n += 1;
    });
    return Array.from(mappa.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((v) => ({ value: v.value, label: `${v.label} (${v.n})` }));
  };

  // I gruppi si ricavano dagli abbonamenti dei clienti in elenco
  const gruppiDisponibili = (() => {
    const mappa = new Map();
    clienti.forEach((c) => {
      (c.gruppi || []).forEach((g) => {
        if (!mappa.has(g.codice)) mappa.set(g.codice, { value: g.codice, label: g.nome || g.codice, n: 0 });
        mappa.get(g.codice).n += 1;
      });
    });
    return Array.from(mappa.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((g) => ({ value: g.value, label: `${g.label} (${g.n})` }));
  })();

  // Le scuole sono state inserite a testo libero, quindi lo stesso
  // istituto compare scritto in modi diversi ("PIZZI" e "SALVATORE
  // PIZZI"). Qui le si accorpa ignorando punteggiatura e parole
  // generiche: senza, filtrando per una scuola se ne perderebbero
  // pezzi di elenco.
  const PAROLE_GENERICHE = new Set([
    'LICEO', 'ISTITUTO', 'TECNICO', 'STATALE', 'SCUOLA', 'ITS', 'ISIS', 'IIS', 'IC',
    'ARTISTICO', 'CLASSICO', 'SCIENTIFICO', 'PROFESSIONALE', 'SUPERIORE', 'DI', 'DEL', 'DELLA'
  ]);

  const paroleScuola = (valore) => {
    const parti = valore.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
    const significative = parti.filter((t) => !PAROLE_GENERICHE.has(t) && t.length > 1);
    return new Set(significative.length ? significative : parti);
  };

  const sottoinsieme = (a, b) => [...a].every((t) => b.has(t));

  const scuole = (() => {
    const grezzi = new Map();
    clienti.forEach((c) => {
      const v = (c.scuola || '').trim();
      if (!v) return;
      grezzi.set(v, (grezzi.get(v) || 0) + 1);
    });

    // Dal nome più lungo al più corto: la forma estesa fa da etichetta
    const gruppi = [];
    [...grezzi.keys()].sort((a, b) => b.length - a.length).forEach((v) => {
      const tok = paroleScuola(v);
      const esistente = gruppi.find(
        (g) => sottoinsieme(tok, g.tok) || sottoinsieme(g.tok, tok)
      );
      if (esistente) {
        esistente.n += grezzi.get(v);
        esistente.varianti.push(v);
        if (v.length > esistente.label.length) esistente.label = v;
      } else {
        gruppi.push({ label: v, tok, varianti: [v], n: grezzi.get(v) });
      }
    });

    // Da valore grezzo a etichetta del gruppo, per filtrare i clienti
    const perValore = new Map();
    gruppi.forEach((g) => g.varianti.forEach((v) => perValore.set(v, g.label)));

    const senza = clienti.filter((c) => !(c.scuola || '').trim()).length;

    const opzioni = gruppi
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((g) => ({ value: g.label, label: `${g.label} (${g.n})` }));

    // In cima: sono quelli su cui si interviene, non un istituto fra gli altri
    if (senza > 0) {
      opzioni.unshift({
        value: SENZA_ISTITUTO,
        label: `Senza istituto assegnato (${senza})`,
      });
    }

    return { perValore, opzioni };
  })();

  const filterConfig = [
    {
      id: 'search',
      label: 'Cerca',
      type: 'text',
      placeholder: 'Nome, cognome, CF, email, telefono...'
    },
    {
      id: 'stato_account',
      label: 'Stato account',
      type: 'select',
      options: [
        { value: 'attivato', label: 'Ha attivato l\'account' },
        { value: 'da_attivare', label: 'Non ha ancora attivato' }
      ]
    },
    {
      id: 'gruppo',
      label: 'Gruppo di linea',
      type: 'multi-select',
      options: gruppiDisponibili
    },
    {
      id: 'scuola',
      label: 'Scuola',
      type: 'multi-select',
      options: scuole.opzioni
    },
    {
      id: 'abbonamento',
      label: 'Abbonamento',
      type: 'select',
      options: [
        { value: 'con', label: 'Con abbonamento' },
        { value: 'senza', label: 'Senza abbonamento' }
      ]
    },
    {
      id: 'citta',
      label: 'Città',
      type: 'multi-select',
      options: valoriDistinti('citta')
    },
    {
      id: 'provincia',
      label: 'Provincia',
      type: 'multi-select',
      options: valoriDistinti('provincia')
    },
    {
      id: 'arretrati',
      label: 'Solo con mensilità arretrate',
      type: 'boolean'
    },
    {
      id: 'senza_telefono',
      label: 'Solo senza telefono',
      type: 'boolean'
    },
    {
      id: 'con_foto',
      label: 'Solo con foto profilo',
      type: 'boolean'
    },
    {
      id: 'anagrafica_incompleta',
      label: 'Solo con anagrafica incompleta',
      type: 'boolean'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header compatto */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gestione titolari</p>
          </div>
          {modificabile && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Nuovo Cliente
          </button>
          )}
        </div>

        {/* Advanced Filters */}
        <AdvancedFilters
          filters={filterConfig}
          onApply={handleApplyFilters}
          resultCount={filteredClienti.length}
        />

        {/* Tabella Clienti - Design compatto */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {filteredClienti.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <Phone className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Nessun cliente trovato</h3>
              <p className="text-sm text-gray-500 mb-4">
                {filteredClienti.length === 0 && clienti.length > 0 
                  ? 'Prova con filtri diversi' 
                  : 'Inizia aggiungendo il primo cliente'}
              </p>
              {filteredClienti.length === 0 && clienti.length === 0 && (
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-lg text-white hover:bg-gray-800 transition text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Cliente
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Contatti
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Indirizzo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Gruppo
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredClienti.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {client.nome} {client.cognome}
                          </div>
                          {client.codice_fiscale && (
                            <div className="text-xs text-gray-500 font-mono mt-0.5">
                              {client.codice_fiscale}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {client.email && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <span className="truncate max-w-xs">{client.email}</span>
                            </div>
                          )}
                          {client.telefono && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              <span>{client.telefono}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {client.indirizzo && client.citta ? (
                          <div className="text-xs text-gray-600 max-w-xs">
                            <div className="truncate">{client.indirizzo}</div>
                            <div className="text-gray-500 truncate">
                              {client.cap} {client.citta} ({client.provincia})
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {client.gruppi?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {client.gruppi.map((g) => (
                              <span
                                key={g.id}
                                className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-50 text-blue-800 border border-blue-200 whitespace-nowrap"
                              >
                                {g.nome || g.codice}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">nessun abbonamento</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/admin/clienti/${client.id}`)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Dettagli"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(client)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Creazione Cliente - Design compatto */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Nuovo Cliente</h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-gray-100 rounded transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-900 font-semibold">📝 Campi obbligatori: Nome, Cognome, Telefono</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    required
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Codice Fiscale</label>
                <input
                  type="text"
                  maxLength={16}
                  value={formData.codice_fiscale}
                  onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 font-mono uppercase"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mario.rossi@email.it"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Usata dal cliente per accedere</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Telefono</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-700 rounded-l-lg text-sm font-semibold">
                      +39
                    </span>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({ ...formData, telefono: value });
                      }}
                      placeholder="3331234567"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Facoltativo - serve per inviare le credenziali via WhatsApp</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Telefono Secondario</label>
                <input
                  type="tel"
                  value={formData.telefono_secondario}
                  onChange={(e) => setFormData({ ...formData, telefono_secondario: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Indirizzo</label>
                <input
                  type="text"
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Città</label>
                  <input
                    type="text"
                    value={formData.citta}
                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">CAP</label>
                    <input
                      type="text"
                      maxLength={5}
                      value={formData.cap}
                      onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Prov</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={formData.provincia}
                      onChange={(e) => setFormData({ ...formData, provincia: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 uppercase"
                      placeholder="RM"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-gray-900 rounded-lg text-white hover:bg-gray-800 transition font-medium"
                >
                  Crea Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal WhatsApp - Design compatto */}
      {showWhatsAppModal && nuovoCliente && nuovoCliente.messaggio_whatsapp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gray-900 px-5 py-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Send className="w-4 h-4" />
                Credenziali di Accesso
              </h2>
              <p className="text-white text-xs mt-0.5 opacity-80">
                Verifica i dati prima di confermare la creazione
              </p>
            </div>

            <div className="p-5 space-y-4">
              {/* Credenziali */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">Credenziali Generate</h3>
                  <button
                    onClick={handleCopyCredenziali}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition text-xs font-medium"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copia
                  </button>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Cliente</p>
                    <p className="text-sm font-bold text-gray-900">{nuovoCliente.nome} {nuovoCliente.cognome}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Email (per l'accesso)</p>
                      <p className="text-sm font-semibold text-gray-900 break-all">{nuovoCliente.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Password Temporanea</p>
                      <p className="text-sm font-semibold text-gray-900 font-mono">{nuovoCliente.password_temporanea}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Senza telefono le credenziali vanno consegnate manualmente */}
              {!nuovoCliente.messaggio_whatsapp.whatsapp_url && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800">
                    Nessun telefono inserito: l'invio via WhatsApp non è disponibile.
                    Usa "Copia" per consegnare le credenziali al cliente.
                  </p>
                </div>
              )}

              {/* Anteprima Messaggio */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 mb-2">Messaggio credenziali</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-sans text-xs text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {nuovoCliente.messaggio_whatsapp.messaggio}
                </div>
              </div>

              {/* Pulsanti */}
              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowWhatsAppModal(false);
                    setNuovoCliente(null);
                  }}
                  className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Annulla
                </button>
                {nuovoCliente.messaggio_whatsapp.whatsapp_url && (
                  <button
                    onClick={() => window.open(nuovoCliente.messaggio_whatsapp.whatsapp_url, '_blank')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-[#25D366] text-white rounded-lg hover:bg-[#20BA5A] transition font-medium"
                  >
                    <Send className="w-4 h-4" />
                    Apri WhatsApp
                  </button>
                )}
                <button
                  onClick={handleConfirmCliente}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium"
                >
                  <CheckCircle className="w-4 h-4" />
                  Conferma e Salva
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                Il cliente verrà creato solo dopo aver cliccato "Conferma e Salva"
              </p>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Clienti;
